import { useCallback } from 'react';
import { View } from 'react-native';

interface UseFullscreenProps {
  containerRef?: React.RefObject<View | null>;
  playerRef: React.RefObject<HTMLVideoElement | HTMLAudioElement | null>;
}

interface UseFullscreenResponse {
  hasFullScreenSupport: () => boolean;
  presentFullscreenPlayer: () => void;
  dismissFullscreenPlayer: () => void;
}

export default ({
  containerRef,
  playerRef,
}: UseFullscreenProps): UseFullscreenResponse => {
  const determineWebFullscreenIsSupported = useCallback(() => {
    return (
      document.fullscreenEnabled ||
      // @ts-ignore
      document.webkitFullscreenEnabled ||
      // @ts-ignore
      document.mozFullscreenEnabled ||
      // @ts-ignore
      document.msFullscreenEnabled
    );
  }, []);

  const determineIosWebFullscreenIsSupported = useCallback(() => {
    // @ts-ignore
    return playerRef?.current?.webkitSupportsFullscreen;
  }, [playerRef]);

  const presentFullscreenWeb = useCallback(() => {
    const playerElement = containerRef?.current;

    if (!playerElement) return;

    // @ts-ignore
    if (playerElement.requestFullscreen) {
      // @ts-ignore
      playerElement.requestFullscreen();
      // @ts-ignore
    } else if (playerElement.webkitRequestFullscreen) {
      // @ts-ignore
      playerElement.webkitRequestFullscreen();
      // @ts-ignore
    } else if (playerElement.mozRequestFullscreen) {
      // @ts-ignore
      playerElement.mozRequestFullscreen();
      // @ts-ignore
    } else if (playerElement.msRequestFullscreen) {
      // @ts-ignore
      playerElement.msRequestFullscreen();
    }
  }, [containerRef]);

  const presentFullscreenIosWeb = useCallback(() => {
    const playerElement = playerRef.current;

    if (!playerElement) return;

    // @ts-ignore
    if (playerElement.webkitEnterFullscreen) {
      // @ts-ignore
      playerElement.webkitEnterFullScreen();
    }
  }, [playerRef]);

  const dismissFullscreenWeb = useCallback(() => {
    const playerElement = containerRef?.current;

    if (!playerElement) return;

    if (document.exitFullscreen) {
      document.exitFullscreen();
      // @ts-ignore
    } else if (document.webkitExitFullscreen) {
      // @ts-ignore
      document.webkitExitFullscreen();
      // @ts-ignore
    } else if (document.mozExitFullscreen) {
      // @ts-ignore
      document.mozExitFullscreen();
      // @ts-ignore
    } else if (document.msExitFullscreen) {
      // @ts-ignore
      document.msExitFullscreen();
    }
  }, [containerRef]);

  const dismissFullscreenIosWeb = useCallback(() => {
    const playerElement = playerRef.current;

    if (!playerElement) return;

    // @ts-ignore
    if (playerElement.webkitExitFullscreen) {
      // @ts-ignore
      playerElement.webkitExitFullscreen();
    }
  }, [playerRef]);

  const hasFullScreenSupport = useCallback(() => {
    return (
      determineWebFullscreenIsSupported() ||
      determineIosWebFullscreenIsSupported()
    );
  }, [determineIosWebFullscreenIsSupported, determineWebFullscreenIsSupported]);

  const presentFullscreenPlayer = useCallback(() => {
    if (determineWebFullscreenIsSupported()) {
      presentFullscreenWeb();
    } else if (determineIosWebFullscreenIsSupported()) {
      presentFullscreenIosWeb();
    }
  }, [
    determineIosWebFullscreenIsSupported,
    determineWebFullscreenIsSupported,
    presentFullscreenIosWeb,
    presentFullscreenWeb,
  ]);

  const dismissFullscreenPlayer = useCallback(() => {
    if (determineWebFullscreenIsSupported()) {
      dismissFullscreenWeb();
    } else if (determineIosWebFullscreenIsSupported()) {
      dismissFullscreenIosWeb();
    }
  }, [
    determineIosWebFullscreenIsSupported,
    determineWebFullscreenIsSupported,
    dismissFullscreenIosWeb,
    dismissFullscreenWeb,
  ]);

  return {
    hasFullScreenSupport,
    presentFullscreenPlayer,
    dismissFullscreenPlayer,
  };
};
